import { FC } from 'react';
import { Group, Modal, Text, ThemeIcon } from '@mantine/core';
import { IconSignature } from '@tabler/icons-react';
import SignatureComponent from '@/PageComponents/Signature/SignatureComponent';
import { NewSignatureRequest } from '@/interfaces/internal/models';
import { Signature } from '@/interfaces/api/models';

// A form-editor-scoped signature modal — deliberately separate from the shared
// `PageComponents/Signature/SignatureModal` (still used by the legacy form editor via
// `components/modals/form/manage-form.tsx`), so this modal's chrome can be modernised without
// touching that legacy consumer. `SignatureComponent` itself is reused as-is: it owns the actual
// capture flow (canvas, QR hand-off, re-sign, terms) and that behaviour must stay identical for
// both callers.
const FormSignatureModal: FC<{
    id?: string;
    request?: NewSignatureRequest;
    onDismiss?: () => void;
    onUpdate: (signature: Signature) => void;
}> = ({ id, request, onDismiss, onUpdate }) => {
    return (
        <Modal
            opened
            onClose={() => onDismiss?.()}
            withCloseButton
            centered
            radius="md"
            // `SignatureComponent`'s canvas (`.signature-pad` in theme/signature-pad.css) is a
            // fixed 590px wide, but its Clear/Confirm button row isn't - it just stretches to
            // fill whatever width the modal body gives it. At the old size (680) that gave the
            // buttons more room than the canvas, so Confirm sat noticeably right of the canvas's
            // own right edge. Sizing the modal so its body content area (size minus 2x the "lg"
            // spacing token this modal uses for body padding, 16px a side) comes out to ~590px
            // makes the button row match the canvas width instead. If `.signature-pad`'s width
            // ever changes, this needs to change with it.
            size={630}
            title={
                <Group gap="xs" wrap="nowrap">
                    <ThemeIcon variant="light" color="scBlue" radius="md" size={32}>
                        <IconSignature size={18} />
                    </ThemeIcon>
                    <Text size="md" fw={600}>Signature</Text>
                </Group>
            }
            overlayProps={{ color: 'var(--mantine-color-scBlue-5)', blur: 10, opacity: 0.55 }}
            transitionProps={{ transition: 'pop', duration: 100, exitDuration: 50, timingFunction: 'ease' }}
            styles={{
                header: { borderBottom: '1px solid var(--mantine-color-gray-2)' },
                body: { padding: 'var(--mantine-spacing-lg)' },
            }}
        >
            <SignatureComponent id={id} request={request} onUpdate={onUpdate} />
        </Modal>
    );
};

export default FormSignatureModal;
